// Text utils

"use strict";

/**
 * Removes acents and diacritics.
 * @param str   The input string
 * @returns     The normalized string
 */
export function normalizeString(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Escapes html reserved characters.
 * @param html      Input HTML text.
 * @returns         The escaped text.
 */
export function escapeHTML(html: string): string {
    return ("" + html)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;")
        .replace(/\//g, "&#x2f;");
}

/**
 * Turns HTML into text (removes all tags)
 * @param html HTML
 * @returns Text
 */
export function htmlToText(html: string): string {
    return (html + "").replace(/<[^>]*>/g, "").trim();
}

/**
 * Escapes single quotes and reverse bars
 * @param raw The raw input text
 * @returns The escaped text.
 */
export function escapeSingleQuotes(raw: string): string {
    return ("" + raw).replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

/**
 * Escapes double quotes and reverse bars.
 * @param raw The raw input text
 * @returns The escaped text.
 */
export function escapeDoubleQuotes(raw: string): string {
    return ("" + raw).replace(/"/g, '\\"').replace(/\\/g, "\\\\");
}

/**
 * Generates highlights for searched text
 * @param text Original text
 * @param query Search query
 * @param cls Class to use for highlights
 * @returns The HTML to display to the user
 */
export function  generateSearchHighlights(text: string, query: string, cls?: string): string {
    if (!query) {
        return escapeHTML(text);
    }
    let result = "";
    let tmp = text;
    const queryLowerCase = query.toLowerCase();
    while (tmp.length > 0) {
        const i = tmp.toLowerCase().indexOf(queryLowerCase);
        if (i === -1) {
            result += escapeHTML(tmp);
            tmp = "";
        } else {
            const preQuery = tmp.substring(0, i);
            const q = tmp.substring(i, i + query.length);
            tmp = tmp.substring(i + query.length);
            result += escapeHTML(preQuery) + '<b class="' + (cls || "shl") + '">' + escapeHTML(q) + '</b>';
        }
    }
    return result;
}

/**
 * Validates a hostname
 * @param host The host or IP address
 * @returns True if valid
 */
export function validateHostname(host: string): boolean {
    try {
        const u = new URL("https://" + host + ":8080/test");
        if (u.protocol !== "https:" || u.hostname !== host) {
            return false;
        }
    } catch (ex) {
        return false;
    }

    return true;
}

/**
 * Validates an URL
 * @param url The URL
 * @param allowedProtocols List of allowed protocols. By default: https and http
 * @returns True if the URL is valid
 */
export function validateURL(url: string, allowedProtocols?: string[]): boolean {
    try {
        const u = new URL(url);
        if (!(allowedProtocols || ['http:', 'https:']).includes(u.protocol)) {
            return false;
        }
    } catch (ex) {
        return false;
    }
    return true;
}

/**
 * Resolves an URL
 * @param path The path
 * @param base The base URL
 * @returns The resolved URL
 */
export function resolveURL(path: string, base: string): string {
    return (new URL(path, base)).toString();
}

/**
 * Validates an email.
 * @param email The email
 */
export function validateEmail(email: string): boolean {
    return (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(email);
}
